import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Grid, Stack, Typography, Paper } from "@mui/material";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import ownerPortrait from "../assets/wildes.jpg";

gsap.registerPlugin(ScrollTrigger);

function AnimatedAccentWord({ text = "conecta", intervalMs = 2000 }) {
  const [outline, setOutline] = useState(false);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) return;

    const t = setInterval(() => setOutline((p) => !p), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);

  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "inline-block",
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{
          color: "var(--verdeClaro)",
          opacity: outline ? 0 : 1,
          transform: outline ? "translateY(6px)" : "translateY(0px)",
          transition: "opacity .35s ease, transform .35s ease",
          display: "inline-block",
        }}
      >
        {text}
      </Box>

      <Box
        component="span"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          color: "transparent",
          WebkitTextStroke: {
            xs: "1.2px var(--verdeClaro)",
            sm: "1.5px var(--verdeClaro)",
            md: "2px var(--verdeClaro)",
          },
          opacity: outline ? 1 : 0,
          transform: outline ? "translateY(0px)" : "translateY(-6px)",
          transition: "opacity .35s ease, transform .35s ease",
          display: "inline-block",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

export default function Sobre() {
  const phrases = useMemo(
    () => ["IMPACTO", "DESEJO", "AUTORIDADE", "PERCEPÇÃO"],
    []
  );

  const [phraseIndex, setPhraseIndex] = useState(0);
  const phraseRef = useRef(null);

  const sectionRef = useRef(null);
  const megaWrapRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const bulletsWrapRef = useRef(null);
  const imageWrapRef = useRef(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) return;

    const t = setInterval(() => {
      setPhraseIndex((p) => (p + 1) % phrases.length);
    }, 2000);

    return () => clearInterval(t);
  }, [phrases.length]);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) return;
    if (!phraseRef.current) return;

    gsap.fromTo(
      phraseRef.current,
      { opacity: 0, y: 26 },
      { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }
    );
  }, [phraseIndex]);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power4.out" },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 78%",
          once: true,
        },
      });

      gsap.set(
        [
          megaWrapRef.current,
          eyebrowRef.current,
          titleRef.current,
          descRef.current,
          bulletsWrapRef.current,
        ],
        {
          opacity: 0,
          y: 30,
        }
      );

      gsap.set(imageWrapRef.current, {
        opacity: 0,
        y: 30,
        scale: 0.985,
      });

      tl.to(megaWrapRef.current, { opacity: 1, y: 0, duration: 0.7 })
        .to(eyebrowRef.current, { opacity: 1, y: 0, duration: 0.45 }, "-=0.35")
        .to(titleRef.current, { opacity: 1, y: 0, duration: 0.55 }, "-=0.30")
        .to(descRef.current, { opacity: 1, y: 0, duration: 0.55 }, "-=0.35")
        .to(
          bulletsWrapRef.current,
          { opacity: 1, y: 0, duration: 0.55 },
          "-=0.40"
        )
        .to(
          imageWrapRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.7 },
          "-=0.65"
        );

      const bulletItems =
        bulletsWrapRef.current?.querySelectorAll?.("[data-bullet]");

      if (bulletItems && bulletItems.length) {
        gsap.set(bulletItems, { opacity: 0, y: 18 });
        tl.to(
          bulletItems,
          {
            opacity: 1,
            y: 0,
            duration: 0.35,
            stagger: 0.08,
            ease: "power3.out",
          },
          "-=0.45"
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Box
      id="sobre"
      ref={sectionRef}
      sx={{
        position: "relative",
        py: { xs: 8, sm: 9, md: 10, lg: 11 },
        bgcolor: "var(--preto)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: {
            xs: "-180px",
            sm: "-120px",
            md: "4%",
            lg: "8%",
            xl: "12%",
          },
          transform: "translateY(-42%)",
          width: {
            xs: 320,
            sm: 420,
            md: 520,
            lg: 620,
            xl: 720,
          },
          height: {
            xs: 320,
            sm: 420,
            md: 520,
            lg: 620,
            xl: 720,
          },
          borderRadius: "50%",
          pointerEvents: "none",
          background:
            "radial-gradient(circle, rgba(172,201,14,0.10) 0%, transparent 70%)",
          filter: "blur(2px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(1200px 800px at 20% 10%, rgba(255,255,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.35))",
        }}
      />

      <Container
        maxWidth={false}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1600,
          mx: "auto",
          px: { xs: 2, md: 3 },
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          columnSpacing={{ xs: 0, md: 5, lg: 7 }}
          rowSpacing={{ xs: 5, sm: 6, md: 4 }}
        >
          {/* TEXTO */}
          <Grid item xs={12} lg={7} order={{ xs: 1, lg: 1 }}>
            <Box
              ref={megaWrapRef}
              aria-hidden
              sx={{
                position: "relative",
                mb: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
                minHeight: {
                  xs: 42,
                  sm: 64,
                  md: 86,
                  lg: 96,
                },
              }}
            >
              <Box
                ref={phraseRef}
                sx={{
                  fontWeight: 950,
                  letterSpacing: { xs: "0.01em", md: "0.02em" },
                  lineHeight: 0.9,
                  textTransform: "uppercase",
                  fontSize: {
                    xs: "clamp(2rem, 9vw, 3rem)",
                    sm: "clamp(3rem, 8vw, 4.4rem)",
                    md: "clamp(4.2rem, 7vw, 6rem)",
                    lg: "clamp(4.5rem, 6vw, 6.5rem)",
                  },
                  opacity: 0.22,
                  color: "transparent",
                  WebkitTextStroke: {
                    xs: "1px rgba(255,255,255,0.22)",
                    sm: "1.2px rgba(255,255,255,0.24)",
                    md: "1.5px rgba(255,255,255,0.25)",
                  },
                  userSelect: "none",
                  pointerEvents: "none",
                  filter: "drop-shadow(0 20px 60px rgba(0,0,0,0.55))",
                  wordBreak: "break-word",
                }}
              >
                {phrases[phraseIndex]}
              </Box>
            </Box>

            <Typography
              ref={eyebrowRef}
              sx={{
                color: "var(--verdeClaro)",
                fontWeight: 950,
                letterSpacing: { xs: "0.18em", sm: "0.22em" },
                textTransform: "uppercase",
                fontSize: { xs: 11, sm: 12, md: 12.5 },
                mb: { xs: 1.5, sm: 2, md: 2.2 },
              }}
            >
              Sobre nós
            </Typography>

            <Typography
              ref={titleRef}
              sx={{
                color: "var(--branco)",
                fontWeight: 1000,
                letterSpacing: "-0.035em",
                lineHeight: { xs: 1.02, sm: 1.03, md: 1.04 },
                fontSize: {
                  xs: "clamp(2.1rem, 10vw, 3rem)",
                  sm: "clamp(3rem, 8vw, 3.8rem)",
                  md: "clamp(3.8rem, 6vw, 4.5rem)",
                  lg: "clamp(4.1rem, 4.8vw, 5.2rem)",
                },
                mb: { xs: 2.5, sm: 2.8, md: 3.2 },
                maxWidth: { xs: "100%", md: 900, xl: 980 },
              }}
            >
              Não registramos.
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: { xs: 0.3, sm: 0.4, md: 0.5 },
                }}
              >
                Criamos imagens <br /> que{" "}
                <AnimatedAccentWord text="provocam" intervalMs={2000} />
              </Box>
            </Typography>

            <Typography
              ref={descRef}
              sx={{
                color: "rgba(255,255,255,0.72)",
                fontSize: {
                  xs: 15,
                  sm: 16,
                  md: 17,
                  lg: 18,
                },
                lineHeight: {
                  xs: 1.8,
                  sm: 1.82,
                  md: 1.85,
                },
                maxWidth: { xs: "100%", md: 720, lg: 700 },
                mb: { xs: 3.2, sm: 3.5, md: 4.2 },
                pr: { xs: 0, md: 1, lg: 2 },
                textAlign: "justify",
              }}
            >
              A Lusco Produtora nasce no instante em que a luz começa. No
              momento em que o olhar ainda não entende exatamente o que vê —
              mas já foi capturado. Não registramos eventos, produtos ou
              experiências. Criamos imagens que fazem o público sentir antes de
              entender.
            </Typography>

            <Stack
              ref={bulletsWrapRef}
              spacing={{ xs: 1.4, sm: 1.6, md: 2 }}
              sx={{
                maxWidth: { xs: "100%", md: 760 },
              }}
            >
              {[
                "Ativamos desejo, pertencimento, urgência e autoridade",
                "Cada projeto começa na energia e termina no impacto",
                "Nada é automático. Nada é feito por fórmula",
              ].map((item) => (
                <Stack
                  key={item}
                  data-bullet
                  direction="row"
                  spacing={{ xs: 1.2, sm: 1.4, md: 1.5 }}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: { xs: 6, md: 7 },
                      height: { xs: 6, md: 7 },
                      mt: "0.55em",
                      borderRadius: 999,
                      bgcolor: "var(--verdeClaro)",
                      boxShadow: {
                        xs: "0 0 0 5px rgba(172,201,14,0.08)",
                        md: "0 0 0 7px rgba(172,201,14,0.08)",
                      },
                      flex: "0 0 auto",
                    }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.88)",
                      fontWeight: 700,
                      fontSize: {
                        xs: 14.5,
                        sm: 15.5,
                        md: 16.5,
                      },
                      lineHeight: 1.6,
                    }}
                  >
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>

          {/* FOTO */}
          <Grid
            item
            xs={12}
            lg={5}
            order={{ xs: 2, lg: 2 }}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", lg: "flex-end" },
              alignItems: "center",
            }}
          >
            <Box
              ref={imageWrapRef}
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: {
                  xs: "center",
                  sm: "center",
                  md: "center",
                  lg: "flex-end",
                },
                mt: { xs: 1, sm: 1.5, md: 0 },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: {
                    xs: "100%",
                    sm: "78%",
                    md: "62%",
                    lg: "100%",
                  },
                  maxWidth: {
                    xs: 320,
                    sm: 360,
                    md: 400,
                    lg: 430,
                    xl: 480,
                  },
                  mx: { xs: "auto", lg: 0 },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: { xs: -8, sm: -10, md: -12, lg: -14 },
                    borderRadius: { xs: 3, md: 4 },
                    pointerEvents: "none",
                    opacity: 0.35,
                    background:
                      "linear-gradient(135deg, rgba(172,201,14,0.30) 0%, transparent 55%)",
                  }}
                />

                <Paper
                  elevation={0}
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "3 / 4",
                    borderRadius: { xs: 3, md: 4 },
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.10)",
                    bgcolor: "rgba(255,255,255,0.02)",
                    boxShadow: "0 20px 80px rgba(0,0,0,0.28)",
                  }}
                >
                  <Box
                    component="img"
                    src={ownerPortrait}
                    alt="Fundador da LSC Produtora"
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 45%)",
                    }}
                  />
                </Paper>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}